import { ElementType, InputHTMLAttributes } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons'
import * as Select from '@radix-ui/react-select'

import Typography from '@/components/base/Typography'

import { cn } from '@/lib/utils'
import { WPGravityFormField } from '@/types'

type FormControlProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'onBlur'
> &
  Partial<
    WPGravityFormField & {
      error?: boolean
      options?: { value: string; text: string }[]
      // eslint-disable-next-line no-unused-vars
      onChange?: (e: any) => void
      // eslint-disable-next-line no-unused-vars
      onBlur?: (e: any) => void
      theme?: 'dark' | 'light'
    }
  >

const FormControl = ({
  id,
  type,
  label,
  checked,
  options,
  placeholder,
  className,
  children,
  theme = 'light',
  error,
  ...props
}: FormControlProps) => {
  const Component = (type === 'textarea' ? 'textarea' : 'input') as ElementType

  return (
    <div
      className={cn(className, {
        hidden: type === 'hidden'
      })}
    >
      {type === 'consent' ? (
        <label className="relative flex items-start space-x-[1rem]">
          <input
            id={id}
            type="checkbox"
            checked={checked}
            className={cn([
              'h-[1.5rem] w-[1.5rem] opacity-0',
              {
                '[&:checked+div]:after:bg-black/80': theme === 'light',
                '[&:checked+div]:after:bg-white/80': theme === 'dark'
              }
            ])}
            {...props}
          />
          <div
            className={cn([
              'absolute left-0 top-0 !m-0 h-[1.5rem] w-[1.5rem] shrink-0 cursor-pointer rounded-full border',
              'after:absolute after:left-[0.2rem] after:top-[0.2rem] after:h-[.9rem] after:w-[.9rem] after:rounded-full',
              {
                'border-black/20': theme === 'light',
                'border-white/20': theme === 'dark',
                'border-red-500': error
              }
            ])}
          />
          <Typography
            variant="small"
            className={cn([
              '[&>#required-data]:mt-[1rem] [&>#required-data]:inline-block',
              {
                'text-dark-500': theme === 'light',
                'text-white': theme === 'dark',
                '!text-red-500': error
              }
            ])}
          >
            {label}
          </Typography>
        </label>
      ) : type === 'select' ? (
        <Select.Root
          value={props.value as string}
          onValueChange={props.onChange}
        >
          <Select.Trigger
            id={id}
            className={cn([
              'flex w-full appearance-none items-center justify-between bg-transparent px-[3rem] py-[1.5rem] text-body outline-none',
              'border',
              'focus:ring-2',
              {
                'text-dark border-black/15': theme === 'light',
                'border-white/20 text-white': theme === 'dark',
                'text-black/15': !props.value && theme === 'light',
                'text-white/20': !props.value && theme === 'dark'
              },
              {
                'focus:border-black/80 focus:ring-transparent':
                  !error && theme === 'light',
                'focus:border-white/80 focus:ring-transparent':
                  !error && theme === 'dark',
                'border-red-500 focus:ring-red-500/50': error
              }
            ])}
          >
            <Select.Value placeholder={placeholder} />
            <Select.Icon>
              <ChevronDownIcon
                className={cn([
                  {
                    'text-dark-500': theme === 'light',
                    'text-white': theme === 'dark'
                  }
                ])}
              />
            </Select.Icon>
          </Select.Trigger>

          <Select.Portal>
            <Select.Content
              data-lenis-prevent
              className={cn([
                'z-50 overflow-hidden rounded-md border shadow-md',
                {
                  'border-black/15 bg-white': theme === 'light',
                  'bg-dark-500 border-white/20': theme === 'dark'
                }
              ])}
            >
              <Select.ScrollUpButton className="flex h-[25px] items-center justify-center">
                <ChevronUpIcon />
              </Select.ScrollUpButton>

              <Select.Viewport>
                {options?.map((option) => (
                  <Select.Item
                    key={option.value}
                    value={option.value}
                    className={cn([
                      'relative flex select-none items-center px-[2rem] py-[0.75rem] text-body outline-none',
                      'hover:cursor-pointer data-[highlighted]:bg-gray-100',
                      {
                        'text-dark-500': theme === 'light',
                        'hover:data-[highlighted]:bg-dark-400 text-white':
                          theme === 'dark'
                      }
                    ])}
                  >
                    <Select.ItemText>{option.text}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>

              <Select.ScrollDownButton className="flex h-[25px] items-center justify-center">
                <ChevronDownIcon />
              </Select.ScrollDownButton>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      ) : (
        <Component
          id={id}
          type={type}
          placeholder={placeholder}
          className={cn([
            'flex w-full appearance-none bg-transparent px-[3rem] py-[1.5rem] text-body outline-none',
            'border',
            'focus:ring-2',
            {
              'text-dark-500 border-black/15 placeholder:text-black/15':
                theme === 'light',
              'border-white/20 text-white placeholder:text-white/20':
                theme === 'dark'
            },
            {
              'focus:border-black/80 focus:ring-transparent':
                !error && theme === 'light',
              'focus:border-white/80 focus:ring-transparent':
                !error && theme === 'dark',
              'border-red-500 focus:ring-red-500/50': error
            }
          ])}
          {...props}
        />
      )}
      {children}
    </div>
  )
}

export default FormControl
